import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { OrganisationResponse } from 'rest/organisations';
import LockFilledIcon from '@atlaskit/icon/glyph/lock-filled';
import UnlockIcon from '@atlaskit/icon/glyph/unlock';
import type { Option, ValueType } from '@atlaskit/select';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import i18n from './messages';
import { IconWrap, OrganisationPicker } from './styled';

export interface Props extends Omit<FieldFormProps<string>, 'label'> {
    onSelectOrg: (org: ValueType<Option<string>>) => void;
    organisations: OrganisationResponse[];
    isLoading: boolean;
    isDisabled: boolean;
    selected: Option<string> | undefined;
}

const privateOption = () => (
    <>
        <IconWrap>
            <LockFilledIcon size="small" label="lock-icon" />
        </IconWrap>
        <FormattedMessage {...i18n.privateRequest} />
    </>
);

const organisationOption = (orgName: string) => (
    <>
        <IconWrap>
            <UnlockIcon size="small" label="unlocked-icon" />
        </IconWrap>
        <FormattedMessage {...i18n.shareWith} values={{ orgName }} />
    </>
);

const renderOption = (option: { label: string; value: string }) => {
    if (!option.value) {
        return privateOption();
    }

    return organisationOption(option.label);
};

const createOption = (org: OrganisationResponse): Option<string> => ({ label: org.name, value: String(org.id) });

export const OrganisationPickerDumb: React.FunctionComponent<Props & InjectedIntlProps> = (props) => {
    const { organisations, description, selected, isLoading, isDisabled, onSelectOrg, id, intl } = props;

    const label = intl.formatMessage(i18n.shareWithLabel);
    const options = [...organisations.map(createOption), { label: intl.formatMessage(i18n.privateRequest), value: '' }];

    return (
        <OrganisationPicker
            id={id}
            required
            label={label}
            description={description}
            options={options}
            value={selected}
            formatOptionLabel={renderOption}
            onChange={onSelectOrg}
            isLoading={isLoading}
            isDisabled={isLoading || isDisabled}
            backspaceRemovesValue={false}
        />
    );
};

export default injectIntl(OrganisationPickerDumb);
